// Default USD to ILS exchange rate (fallback if API fails)
const DEFAULT_USD_TO_ILS_RATE = 3.15;

// Cache duration: 1 hour (rates don't change that frequently)
const CACHE_DURATION_MS = 60 * 60 * 1000;

let cachedRate: number | null = null;
let cacheTimestamp: number | null = null;
let rateFetchPromise: Promise<number> | null = null;

export async function getExchangeRate(): Promise<number> {
  // Return cached rate if still valid
  if (cachedRate !== null && cacheTimestamp !== null) {
    const isExpired = Date.now() - cacheTimestamp > CACHE_DURATION_MS;
    if (!isExpired) {
      return cachedRate;
    }
  }

  if (rateFetchPromise) {
    return rateFetchPromise;
  }

  rateFetchPromise = fetchExchangeRate();
  return rateFetchPromise;
}

async function fetchExchangeRate(): Promise<number> {
  try {
    // Free currency API - no API key required
    const response = await fetch('https://api.exchangerate-api.com/v4/latest/USD');

    if (response.ok) {
      const data = await response.json();
      if (data.rates?.ILS) {
        cachedRate = Number(data.rates.ILS);
        cacheTimestamp = Date.now();
        rateFetchPromise = null;
        return cachedRate;
      }
    }
  } catch (error) {
    console.warn('Failed to fetch live exchange rate:', error);
  }

  // Fallback: try settings API
  try {
    const apiUrl = import.meta.env.VITE_API_URL || '/api';
    const response = await fetch(`${apiUrl}/content/settings/usd_to_ils_rate`);

    if (response.ok) {
      const data = await response.json();
      if (data.success && data.data?.value) {
        cachedRate = Number(data.data.value);
        cacheTimestamp = Date.now();
        rateFetchPromise = null;
        return cachedRate;
      }
    }
  } catch (error) {
    console.warn('Failed to fetch settings exchange rate, using default:', error);
  }

  cachedRate = DEFAULT_USD_TO_ILS_RATE;
  cacheTimestamp = Date.now();
  rateFetchPromise = null;
  return cachedRate;
}

export function convertUsdToIls(usdAmount: number, rate: number): number {
  return Math.round(usdAmount * rate * 100) / 100;
}

export function formatIlsPrice(amount: number): string {
  return `₪${amount.toFixed(2)}`;
}

// Synchronous version using cached rate or default
export function convertUsdToIlsSync(usdAmount: number): number {
  const rate = cachedRate ?? DEFAULT_USD_TO_ILS_RATE;
  return convertUsdToIls(usdAmount, rate);
}

// Initialize the rate on app load
export function initializeExchangeRate(): void {
  getExchangeRate().catch(console.error);
}
